<template>
  <div class="data-table">
    <q-table
      ref="datatable"
      flat
      :data="tableData"
      :columns="columns"
      :row-key="rowKey"
      :filter="searchQuery"
      :filter-method="filterMethod"
      :selection="selectable ? 'multiple' : 'none'"
      :selected.sync="currentSelectedItems"
      :selected-rows-label="getSelectedString"
      :rowsPerPageOptions="rowsPerPageOptions"
      :visible-columns="visibleColumns"
      :table-class="`table ${hidePagination ? 'border-bottom' : ''}`"
      separator="cell"
      :dense="dense"
      wrap-cells
      :hide-pagination="hidePagination"
      :pagination.sync="pagination"
      :fullscreen.sync="isFullscreen"
    >
      <template v-slot:top>
        <div class="table-head-contents" v-if="!hideActionRow">
          <!--First row-->
          <div class="top-first-row" v-if="$slots['topFirstRow']">
            <slot name="topFirstRow" />
          </div>

          <div class="top-second-row">
            <div class="search-bar">
              <SearchBar v-model="searchQuery" :placeholder="filterPlaceholder" clearable />
            </div>

            <div class="table-actions">
              <slot name="extraActions" :data="tableData"></slot>

              <ExportButton
                :conditions="exportConditions"
                :loading="exportLoading"
                @click="handleExport"
                v-if="exportConditions.length"
              />

              <!-- Global Action Menu Button -->
              <GlobalActionMenu
                :options="currentGlobalActionMenuOptions"
                @click="handleClickGlobalAction"
              />
            </div>
          </div>

          <div class="extra-row">
            <slot name="beforeBody" :data="tableData" />
          </div>
        </div>
      </template>
      <!-- pass through scoped slots -->
      <template v-for="(_, scopedSlotName) in $scopedSlots" v-slot:[scopedSlotName]="props">
        <slot :name="scopedSlotName" :props="props" />
      </template>

      <!-- pass through normal slots -->
      <template v-for="(_, slotName) in $slots" v-slot:[slotName]>
        <slot :name="slotName" />
      </template>
    </q-table>
    <Loading :loading="loading" />

    <DialogVisibleColumn v-model="openDialogVisibleColumn" :columns.sync="currentColumnItems" />
  </div>
</template>

<script>
import SearchBar from '../SearchBar';
import Loading from '../Loading';
import ExportButton from './ExportButton';
import GlobalActionMenu from './GlobalActionMenu';
import DialogVisibleColumn from './DialogVisibleColumn';

const messages = {
  en: {
    'Change display columns': 'Change display columns',
    '{selected} record(s) selected of {dataLength}.':
      '{selected} record(s) selected of {dataLength}.',
    'Change the type of columns displayed in the table.':
      'Change the type of columns displayed in the table.',
    'Download as CSV': 'Download as CSV',
  },
  ja: {
    'Change display columns': '表示項目の変更',
    '{selected} record(s) selected of {dataLength}.': '{selected}個のアイテムを選択しています。',
    'Change the type of columns displayed in the table.':
      'テーブルに表示するカラムの種類を変更します。',
    'Download as CSV': 'Download as CSV',
  },
};
export default {
  name: 'DataTable',
  i18n: { messages },
  components: {
    SearchBar,
    Loading,
    ExportButton,
    GlobalActionMenu,
    DialogVisibleColumn,
  },
  props: {
    loading: Boolean,
    // Table
    name: { type: String, required: true },
    data: {
      type: Array,
      default: () => {
        return [];
      },
      required: true,
    },
    columns: {
      type: Array,
      default: () => {
        return [];
      },
      required: true,
    },
    columnItems: {
      type: Array,
      default: () => {
        return [];
      },
      required: true,
    },
    rowKey: {
      type: [String, Number],
      default: '__index',
    },
    defaultSort: String,

    // Pagination
    rowsPerPageOptions: {
      type: Array,
      default: () => {
        return [25, 50, 100, 200, 0];
      },
    },

    rowsPerPage: {
      type: Number,
      default: 25,
    },

    // Style
    dense: {
      type: Boolean,
    },

    // Table Select Box
    selectable: Boolean,
    selectedItems: {
      type: Array,
      default: () => {
        return [];
      },
    },
    // Search bar
    filterPlaceholder: String,
    filterMethod: {
      type: Function,
    },
    // Global Action Menu
    // [{ label: String, action: String, icon: String, clickable: Boolean, isChecked: Boolean,}]
    globalActionMenuOptions: {
      type: Array,
      default: () => {
        return [];
      },
    },
    // Export
    exportConditions: {
      type: Array,
      default: () => {
        return [];
      },
    },
    exportLoading: {
      type: Boolean,
      default: false,
    },

    hideActionRow: {
      type: Boolean,
    },
    hidePagination: {
      type: Boolean,
    },
  },
  data() {
    return {
      // Search Box
      searchQuery: '',
      // Pagination
      pagination: {
        rowsPerPage: 25,
      },
      // Full Screen
      isFullscreen: false,
      savedData: null,
      // Global Actions - Open Dialog
      openDialogVisibleColumn: false,

      // Route meta group
      routeMetaGroup: null,
    };
  },
  watch: {
    columnItems() {
      this.updateColumnItems();
    },
    currentColumnItems: {
      handler: function (val) {
        const data = val.map((col) => {
          return {
            key: col.key,
            visible: col.visible,
          };
        });
        localStorage.setItem(`table-visibleColumn-${this.name}`, JSON.stringify(data));
      },
      deep: true,
    },
    data() {
      this.clearSelection();
    },
    searchQuery(val) {
      this.$emit('changeSearchQuery', val);
    },
    tableData(val) {
      this.$emit('update:table-data', val);
    },
  },
  created() {
    this.updateColumnItems();
  },
  mounted() {
    if (this.defaultSort) {
      this.$refs['datatable'].sort(this.defaultSort);
    }
  },
  computed: {
    currentColumnItems() {
      return this.savedData;
    },
    visibleColumns() {
      const result = [];
      this.currentColumnItems.forEach((val) => {
        if (val.visible) result.push(val.key);
      });
      return result;
    },
    currentSelectedItems: {
      set(items) {
        this.$emit('update:selectedItems', items);
      },
      get() {
        return this.selectedItems;
      },
    },
    tableData() {
      if (this.loading) return [];
      return this.data;
    },
    currentGlobalActionMenuOptions() {
      return [
        ...this.globalActionMenuOptions,
        {
          label: this.$t('Change display columns'),
          description: this.$t('Change the type of columns displayed in the table.'),
          action: 'changeColumn',
          icon: 'tune',
        },
      ];
    },
  },
  methods: {
    updateColumnItems() {
      const storage = JSON.parse(localStorage.getItem(`table-visibleColumn-${this.name}`));
      const columnsMapping = this.columns.reduce((agg, cur) => {
        agg[cur.name] = cur;
        return agg;
      }, {});
      const cleanedColumnItems = this.columnItems.map((item) => {
        if (typeof item === 'string') {
          return {
            key: item,
            label: (columnsMapping[item] || {}).label,
            visible: true,
          };
        }
        return item;
      });
      if (storage) {
        this.savedData = cleanedColumnItems.map((val) => {
          const visible = storage.find((data) => data.key === val.key);
          if (visible) {
            return {
              label: val.label,
              key: val.key,
              visible: visible['visible'],
            };
          }
          return {
            label: val.label,
            key: val.key,
            visible: true,
          };
        });
      } else {
        this.savedData = cleanedColumnItems;
      }
    },
    handleExport(condition) {
      if (condition.page === 'table-data') {
        this.$emit('downloadTable', condition.data);
      } else if (condition.page === 'table-data-csv') {
        const data = [];
        data.push(this.columnItems.map((column) => column.label).join(','));
        this.tableData.forEach((row) => {
          data.push(this.columnItems.map((column) => row[column.key]).join(','));
        });

        const url = window.URL.createObjectURL(new Blob([data.join('\n')]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', condition.fileName);
        document.body.appendChild(link);
        link.click();
      } else {
        this.$emit('handleExportFallback', condition);
      }
    },
    handleClickGlobalAction(type) {
      if (type === 'changeColumn') {
        this.openDialogVisibleColumn = true;
      } else {
        this.$emit('global-action', type);
      }
    },
    getSelectedString(selected) {
      return this.$t('{selected} record(s) selected of {dataLength}.', {
        selected: selected,
        dataLength: this.data.length,
      });
    },
    setSearchQuery(val) {
      this.searchQuery = val;
    },
    clearSelection() {
      this.$refs['datatable'].clearSelection();
    },
  },
};
</script>

<style lang="scss" scoped>
@import '~quasar/src/css/variables';

.data-table /deep/ {
  .table {
    border-left: 1px solid $separator-color;
    border-right: 1px solid $separator-color;

    &.border-bottom {
      border-bottom: 1px solid $separator-color;
      border-radius: 0;
    }
  }
  .q-table__top {
    padding: 0;
  }
  .q-table tbody tr {
    background: none !important;
  }
  .q-table tbody tr:hover {
    background: none !important;
  }
  .q-table th {
    font-weight: map-get($text-weights, 'bold');
  }
}

.data-table {
  position: relative;
  font-size: map-get($body1, 'size');

  &--top-first-row {
    display: inline-block;
    margin-right: $space-base;
  }
}

.table-head-contents {
  width: 100%;
  margin-bottom: $space-base;
}

.top-first-row {
  display: flex;
  align-items: center;
  margin-bottom: $space-base;
}

.top-second-row {
  display: flex;
  align-items: center;
  .search-bar {
    flex-grow: 1;
    margin-right: $space-base * 1.5;
  }
  .table-actions {
    display: flex;
    align-items: center;
  }
}
</style>
